import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type RowSelectionState,
} from '@tanstack/react-table'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  areasQuery,
  meQuery,
  ordersInfiniteQuery,
  type AreaGroupBy,
  type OrderSummary,
} from '@/api/queries'
import { EmptyState, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { AmpelBadge } from '@/features/orders/AmpelBadge'
import { ampelOf } from '@/features/orders/ampel'
import { BundleDialog } from '@/features/orders/BundleDialog'
import {
  GROUP_BY,
  orderSearchSchema,
  toListParams,
  type OrderSearch,
} from '@/features/orders/filters'
import { OrderFilterBar } from '@/features/orders/OrderFilterBar'
import { requireNav } from '@/nav/guard'

export const Route = createFileRoute('/_app/orders/')({
  beforeLoad: requireNav('orders'),
  validateSearch: orderSearchSchema,
  component: OrdersPage,
})

const column = createColumnHelper<OrderSummary>()

function OrdersPage() {
  const { t, i18n } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: me } = useQuery(meQuery)
  const canBundle = me?.permissions.includes('order.assign') ?? false
  const isClient = me?.organization.kind === 'CLIENT'

  const update = useCallback(
    (patch: Partial<OrderSearch>) =>
      void navigate({
        search: (prev) => ({ ...prev, ...patch }),
        replace: true,
      }),
    [navigate],
  )

  const query = useInfiniteQuery(ordersInfiniteQuery(toListParams(search)))
  const loaded = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data])
  // Frist-Ampel gibt es serverseitig nicht: auf den geladenen Seiten filtern
  const rows = useMemo(
    () =>
      search.ampel ? loaded.filter((o) => ampelOf(o.nextDeadlineAt) === search.ampel) : loaded,
    [loaded, search.ampel],
  )

  const [selection, setSelection] = useState<RowSelectionState>({})
  const [bundleOpen, setBundleOpen] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const selectedIds = Object.keys(selection).filter((id) => selection[id])

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    )

  // Auftraggeber sehen die Nachunternehmer nicht (Spalte entfällt)
  const subColumn = column.accessor('assignedOrgId', {
    header: () => t('orders.colAssigned'),
    cell: ({ getValue }) =>
      getValue() ? t('orders.subShort', { id: String(getValue()).slice(0, 8) }) : t('orders.noSub'),
  })

  const columns = useMemo(
    () => [
      ...(canBundle
        ? [
            column.display({
              id: 'select',
              header: ({ table }) => (
                <Checkbox
                  aria-label={t('orders.selectAll')}
                  checked={
                    table.getIsAllRowsSelected()
                      ? true
                      : table.getIsSomeRowsSelected()
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={(on) => table.toggleAllRowsSelected(on === true)}
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  aria-label={t('orders.selectRow', { number: row.original.number })}
                  checked={row.getIsSelected()}
                  onCheckedChange={(on) => row.toggleSelected(on === true)}
                />
              ),
            }),
          ]
        : []),
      column.accessor('number', {
        header: () => t('orders.colNumber'),
        cell: ({ row }) => (
          <div>
            <Link
              to="/orders/$orderId"
              params={{ orderId: row.original.id }}
              className="inline-flex min-h-11 items-center font-medium text-primary underline"
            >
              {row.original.number}
            </Link>
            {row.original.externalRef && (
              <div className="text-sm text-muted-foreground">{row.original.externalRef}</div>
            )}
          </div>
        ),
      }),
      column.accessor('status', {
        header: () => t('orders.colStatus'),
        cell: ({ getValue }) => <Badge variant="secondary">{t(`orderStatus.${getValue()}`)}</Badge>,
      }),
      column.accessor('orderType', {
        header: () => t('orders.colType'),
        cell: ({ getValue }) => getValue() ?? t('common.none'),
      }),
      column.accessor('address', {
        header: () => t('orders.colAddress'),
        cell: ({ getValue }) => getValue() ?? t('common.none'),
      }),
      column.accessor((o) => o.rajon ?? o.municipality ?? o.postalCode ?? o.mdf, {
        id: 'area',
        header: () => t('orders.colArea'),
        cell: ({ getValue }) => getValue() ?? t('common.none'),
      }),
      ...(isClient ? [] : [subColumn]),
      column.accessor('nextDeadlineAt', {
        header: () => t('orders.colDeadline'),
        cell: ({ row, getValue }) => (
          <div>
            <AmpelBadge ampel={ampelOf(row.original.nextDeadlineAt)} />
            {getValue() && (
              <div className="text-sm text-muted-foreground">{formatDate(String(getValue()))}</div>
            )}
          </div>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, canBundle, isClient, i18n.language],
  )

  // TanStack Table ist nicht memoisierbar; die Tabelle wird hier nicht weitergereicht, daher unkritisch
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (order) => order.id,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true, // Cursor-Pagination läuft serverseitig
    manualFiltering: true,
    state: { rowSelection: selection },
    onRowSelectionChange: setSelection,
    enableRowSelection: canBundle,
  })

  return (
    <>
      <PageHeader title={t('orders.title')} description={t('orders.description')}>
        {canBundle && (
          <Button
            type="button"
            disabled={selectedIds.length === 0}
            onClick={() => setBundleOpen(true)}
          >
            {t('bundle.action')}
            {selectedIds.length > 0 &&
              ` (${t('orders.selectedCount', { count: selectedIds.length })})`}
          </Button>
        )}
      </PageHeader>

      {notice && (
        <p role="status" className="mb-4 rounded-md border bg-card p-3">
          {notice}
        </p>
      )}

      <AreasPanel search={search} onChange={update} />
      <OrderFilterBar search={search} onChange={update} rows={loaded} hideSub={isClient} />

      {query.isPending ? (
        <Skeleton className="h-32 w-full" aria-label={t('common.loading')} />
      ) : query.isError ? (
        <EmptyState title={t('errors.generic')} body={t('errors.network')}>
          <Button variant="outline" onClick={() => void query.refetch()}>
            {t('common.retry')}
          </Button>
        </EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState title={t('orders.emptyTitle')} body={t('orders.emptyBody')} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-left" aria-label={t('orders.listLabel')}>
              <thead className="bg-muted text-sm">
                {table.getHeaderGroups().map((group) => (
                  <tr key={group.id}>
                    {group.headers.map((header) => (
                      <th key={header.id} scope="col" className="px-4 py-3">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t"
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <p className="text-sm text-muted-foreground" role="status">
              {t('orders.loadedCount', { count: loaded.length })}
              {!query.hasNextPage && ` · ${t('orders.allLoaded')}`}
            </p>
            {query.hasNextPage && (
              <Button
                variant="outline"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? t('common.loading') : t('orders.loadMore')}
              </Button>
            )}
          </div>
        </>
      )}

      {canBundle && bundleOpen && (
        <BundleDialog
          open
          orderIds={selectedIds}
          onOpenChange={(open) => !open && setBundleOpen(false)}
          onDone={(message) => {
            setNotice(message)
            setSelection({})
          }}
        />
      )}
    </>
  )
}

/** A-28: Aufträge nach Gebiet gruppiert (offen, nächste Frist, offene Gruben); Klick filtert die Liste. */
function AreasPanel({
  search,
  onChange,
}: {
  search: OrderSearch
  onChange: (patch: Partial<OrderSearch>) => void
}) {
  const { t, i18n } = useTranslation()
  const groupBy: AreaGroupBy = search.groupBy ?? 'rajon'
  const areas = useQuery(areasQuery(groupBy))
  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    )

  return (
    <section aria-labelledby="areas-title" className="mb-6">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
        <h2 id="areas-title" className="text-lg font-medium">
          {t('orders.areasTitle')}
        </h2>
        <div className="grid gap-1">
          <label htmlFor="group-by" className="text-sm font-medium">
            {t('orders.groupBy')}
          </label>
          <select
            id="group-by"
            className="h-11 min-w-48 rounded-md border border-input bg-card px-3 text-base"
            value={groupBy}
            onChange={(event) =>
              onChange({ groupBy: event.target.value as AreaGroupBy, area: undefined })
            }
          >
            {GROUP_BY.map((value) => (
              <option key={value} value={value}>
                {t(`orders.groupByValue.${value}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {areas.isPending ? (
        <Skeleton className="h-16 w-full" aria-label={t('common.loading')} />
      ) : areas.isError ? (
        <p className="text-sm text-destructive">{t('errors.network')}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-left" aria-label={t('orders.areasTitle')}>
            <thead className="bg-muted text-sm">
              <tr>
                <th scope="col" className="px-4 py-2">
                  {t('orders.areaName')}
                </th>
                <th scope="col" className="px-4 py-2">
                  {t('orders.areaOpen')}
                </th>
                <th scope="col" className="px-4 py-2">
                  {t('orders.areaTotal')}
                </th>
                <th scope="col" className="px-4 py-2">
                  {t('orders.areaNext')}
                </th>
                <th scope="col" className="px-4 py-2">
                  {t('orders.areaPits')}
                </th>
                <th scope="col" className="px-4 py-2">
                  <span className="sr-only">{t('orders.areaFilter')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {areas.data.map((area) => (
                <tr key={area.key} className="border-t" data-area={area.key}>
                  <th scope="row" className="px-4 py-2 font-medium">
                    {area.key || t('orders.areaNone')}
                  </th>
                  <td className="px-4 py-2">{area.openCount}</td>
                  <td className="px-4 py-2">{area.totalCount}</td>
                  <td className="px-4 py-2">
                    {area.nextDeadlineAt ? (
                      <AmpelBadge ampel={ampelOf(area.nextDeadlineAt)} />
                    ) : (
                      t('common.none')
                    )}
                    {area.nextDeadlineAt && (
                      <div className="text-sm text-muted-foreground">
                        {formatDate(area.nextDeadlineAt)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">{area.openPits}</td>
                  <td className="px-4 py-2">
                    {area.key && (
                      <Button
                        type="button"
                        variant={search.area === area.key ? 'default' : 'outline'}
                        size="sm"
                        aria-pressed={search.area === area.key}
                        aria-label={`${t('orders.areaFilter')}: ${area.key}`}
                        onClick={() =>
                          onChange({ area: search.area === area.key ? undefined : area.key })
                        }
                      >
                        {t('orders.areaFilter')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
